import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import { useStaticQuery, graphql, Link } from 'gatsby';
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from '@reach/combobox';

import { resizeShopifyImage } from '../utils';
import { useGraphQL } from '../hooks';

export function SearchModal({ isModalOpen, setIsModalOpen }) {
  const {
    allShopifyProduct: { products },
  } = useStaticQuery(graphql`
    {
      allShopifyProduct(
        sort: { fields: updatedAt, order: DESC }
        filter: { availableForSale: { eq: true } }
      ) {
        products: nodes {
          handle
          images {
            originalSrc
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          productType
          tags
          title
          updatedAt
          variants {
            shopifyId
          }
        }
      }
    }
  `);

  const close = () => setIsModalOpen(false);

  // Add state for query
  const [query, setQuery] = useState('');

  // Add state for search results
  const [searchResults, setSearchResults] = useState([]);

  // When query changes, update the state
  function handleChange(e) {
    setQuery(e.target.value);
  }

  useEffect(() => {
    // Whenever query is not an empty string, update the productResults
    if (query !== '') {
      const results = products.filter((product) =>
        product.title.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
    }
    // If the user starts typing, and then deletes their query, set it back to an empty array
    else setSearchResults([]);
  }, [products, query]);

  const { placeholderImage } = useGraphQL();

  if (!isModalOpen) return null;

  return (
    <DialogOverlay onDismiss={close} className="fixed inset-0 z-40">
      <div className="fixed inset-0 z-40 flex justify-end bg-black bg-opacity-50">
        <DialogContent
          aria-label="Search"
          className="fixed inset-x-0 top-0 px-4 py-16 bg-white text-dark-pink"
        >
          <div className="flex w-full max-w-3xl mx-auto">
            <div className="relative w-full">
              <Combobox>
                <div className="relative shadow-sm">
                  <ComboboxInput
                    onChange={handleChange}
                    value={query}
                    placeholder="Search"
                    autoComplete="false"
                    className="block w-full pr-10 rounded-none form-input sm:text-sm sm:leading-5"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="w-5 h-5 text-gray-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
                <ComboboxPopover portal={false}>
                  {!!searchResults.length && (
                    <div className="absolute inset-x-0 z-10 p-4 text-left bg-white border top-9">
                      <h3 className="px-4 font-medium tracking-wider uppercase">
                        Products
                      </h3>
                      <ComboboxList className="mt-4 border-t">
                        {searchResults.slice(0, 6).map((result) => (
                          <ComboboxOption
                            key={result.handle}
                            value={result.title}
                            className="-mx-4 hover:bg-gray-200 odd:bg-gray-50"
                          >
                            <Link
                              to={`/products/${result.handle}`}
                              className="flex items-center px-8 py-2"
                            >
                              <img
                                src={
                                  result.images[0]
                                    ? resizeShopifyImage({
                                        url: result.images[0].originalSrc,
                                        width: 44,
                                      })
                                    : placeholderImage.publicURL
                                }
                                alt=""
                                width={44}
                                height={44}
                                className="object-contain bg-white h-11 w-11"
                              />
                              <span className="ml-2 capitalize">
                                {result.title}
                              </span>
                            </Link>
                          </ComboboxOption>
                        ))}
                      </ComboboxList>
                      <div className="px-4 pt-4 border-t">
                        Searching for: "{query}"
                      </div>
                    </div>
                  )}
                </ComboboxPopover>
              </Combobox>
            </div>
            <button type="button" onClick={close} className="ml-4 text-2xl">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </DialogContent>
      </div>
    </DialogOverlay>
  );
}

SearchModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
};
